import React, { useState, useEffect, useCallback } from 'react';
import { doc, setDoc, getDoc, collection, getDocs } from 'firebase/firestore';
import { User, ArrowLeft, Save, Trash, Plus } from 'lucide-react';
import { useAdvancedPermissions } from '../hooks/useAdvancedPermissions';
import { useTheme } from '../contexts/ThemeContext';
import { TARIFS_REFERENCE } from '../services/tarificationService';

const TIME_SLOTS = [
  { id: 'morning', label: 'Matin', timeRange: '04:00 - 12:00' },
  { id: 'noon', label: 'Midi', timeRange: '12:00 - 14:00' },
  { id: 'afternoon', label: 'Après-midi', timeRange: '14:00 - 17:30' },
  { id: 'evening', label: 'Soir', timeRange: '17:30 - 22:00' },
];

// Fonction utilitaire pour calculer les frais mandat OMAD
const calculateOmadFees = (weeklyHours) => {
  if (weeklyHours <= 10) return 6.00;
  if (weeklyHours <= 25) return 5.40;
  if (weeklyHours <= 40) return 4.20;
  return 3.00;
};

const ClientContract = ({ client, db, currentUser }) => {
  const { theme } = useTheme();

  // Vérification de sécurité pour client null
  if (!client) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-8 text-center">
            <User size={64} className="mx-auto mb-6 text-gray-400 dark:text-gray-500" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Client non sélectionné</h2>
            <p className="text-gray-600 dark:text-gray-400 text-lg mb-6">
              Veuillez sélectionner un client depuis la liste des bénéficiaires pour accéder au contrat.
            </p>
            <button
              onClick={() => window.history.back()}
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <ArrowLeft size={20} className="mr-2" />
              Retour aux bénéficiaires
            </button>
          </div>
        </div>
      </div>
    );
  }

  const [contract, setContract] = useState({
    startDate: '',
    endDate: '',
    isOpenEnded: false,
    hourlyRate: '',
    hoursPerMonth: '',
    tasksPerPeriod: {
      morning: [],
      noon: [],
      afternoon: [],
      evening: []
    },
    customTasks: '',

    // Nouvelles données tarifaires (paramètres de base pour le contrat)
    contractType: 'prestation', // 'prestation', 'mandat', 'mise_en_relation'

    // NOUVEAU SYSTÈME : Liste des prestations
    prestations: [], // { id, type, weeklyHours, baseRateType }

    // Legacy fields (gardés pour compatibilité lors de la migration)
    weeklyHours: 0,
    baseRateType: 'standard', // 'standard' ou '70plus'

    majorations: {
      weekend: false,
      holidays: false,
      may1: false
    },
    termination: {
      noticeDays: 30,
      conditions: 'lettre recommandée avec accusé de réception'
    },
    modifications: {
      noticeHours: 48,
      conditions: 'téléphone, SMS ou mail'
    }
  });
  const [categories, setCategories] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState({});

  // Utiliser le nouveau système de permissions avancées
  const { hasPermission: hasAdvancedPermission, loading: permissionsLoading } = useAdvancedPermissions({ userId: currentUser?.id });

  // Définir les permissions avec le nouveau système
  const canViewContracts = hasAdvancedPermission({ resource: 'contracts', action: 'read' });
  const canEditContracts = hasAdvancedPermission({ resource: 'contracts', action: 'write' });
  const canManageContracts = hasAdvancedPermission({ resource: 'contracts', action: 'manage' });

  const fetchContract = useCallback(async () => {
    const contractDoc = await getDoc(doc(db, 'contracts', client.id));
    if (contractDoc.exists()) {
      const data = contractDoc.data();
      setContract({
        ...data,
        tasksPerPeriod: data.tasksPerPeriod || { morning: [], noon: [], afternoon: [], evening: [] },
        majorations: data.majorations || { weekend: false, holidays: false, may1: false },
        termination: data.termination || { noticeDays: 30, conditions: 'lettre recommandée avec accusé de réception' },
        modifications: data.modifications || { noticeHours: 48, conditions: 'téléphone, SMS ou mail' },
        prestations: data.prestations || [] // Charger les prestations existantes
      });
    }
  }, [db, client.id]);

  const fetchCategories = useCallback(async () => {
    const categorySnapshot = await getDocs(collection(db, 'taskCategories'));
    const fetchedCategories = categorySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setCategories(fetchedCategories);
  }, [db]);

  const fetchTasks = useCallback(async () => {
    const taskSnapshot = await getDocs(collection(db, 'tasks'));
    const fetchedTasks = taskSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setTasks(fetchedTasks);
  }, [db]);

  useEffect(() => {
    fetchContract();
    fetchCategories();
    fetchTasks();
  }, [fetchContract, fetchCategories, fetchTasks]);

  // Migration des données legacy vers le nouveau format prestations
  useEffect(() => {
    // Si on a des données legacy (weeklyHours > 0) mais pas de prestations tableau
    if (contract.weeklyHours > 0 && (!contract.prestations || contract.prestations.length === 0)) {
      console.log('Migration des données legacy vers prestations...');
      setContract(prev => ({
        ...prev,
        prestations: [{
          id: crypto.randomUUID(),
          type: 'assistance_vie', // Par défaut
          weeklyHours: prev.weeklyHours,
          baseRateType: prev.baseRateType
        }]
      }));
    }
  }, [contract.weeklyHours, contract.baseRateType, contract.prestations]);

  // Gestion des prestations
  const addPrestation = () => {
    setContract(prev => ({
      ...prev,
      prestations: [
        ...prev.prestations,
        {
          id: crypto.randomUUID(),
          type: 'assistance_vie',
          weeklyHours: 10,
          baseRateType: 'standard'
        }
      ]
    }));
  };

  const removePrestation = (id) => {
    setContract(prev => ({
      ...prev,
      prestations: prev.prestations.filter(p => p.id !== id)
    }));
  };

  const updatePrestation = (id, field, value) => {
    setContract(prev => ({
      ...prev,
      prestations: prev.prestations.map(p =>
        p.id === id ? { ...p, [field]: value } : p
      )
    }));
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setContract(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleTaskChange = (taskId, period) => {
    setContract(prev => {
      const newSelectedTasks = new Set(prev.tasksPerPeriod[period]);
      if (newSelectedTasks.has(taskId)) {
        newSelectedTasks.delete(taskId);
      } else {
        newSelectedTasks.add(taskId);
      }
      return {
        ...prev,
        tasksPerPeriod: {
          ...prev.tasksPerPeriod,
          [period]: Array.from(newSelectedTasks)
        }
      };
    });
  };

  const handleCategoryExpand = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canEditContracts) return;

    try {
      console.log('Sauvegarde du contrat...', contract);
      await setDoc(doc(db, 'contracts', client.id), contract);
      console.log('Contrat sauvegardé avec succès !');
      // Idéalement utiliser un toast ici, mais pour l'instant un alert simple pour debug
      alert('Contrat sauvegardé avec succès !');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du contrat:', error);
      alert('Erreur lors de la sauvegarde : ' + error.message);
    }
  };

  // Vérifier l'accès aux contrats
  if (permissionsLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Chargement des permissions...</p>
        </div>
      </div>
    );
  }

  if (!canViewContracts) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-8 text-center">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-2xl">🚫</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Accès refusé</h2>
            <p className="text-gray-600 dark:text-gray-400 text-lg mb-6">
              Vous n'avez pas les permissions nécessaires pour accéder aux contrats.
            </p>
            <button
              onClick={() => window.history.back()}
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <ArrowLeft size={20} className="mr-2" />
              Retour
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Function to render tasks by time slot
  const renderTasksByTimeSlot = (timeSlot) => (
    <div className="space-y-3">
      {categories.map(category => (
        <div key={category.id} className="border border-gray-200 dark:border-gray-600 rounded-lg p-3 bg-white dark:bg-gray-700">
          <div className="flex justify-between items-center">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white">{category.name}</h4>
            <button
              type="button"
              onClick={() => handleCategoryExpand(category.id)}
              className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
            >
              {expandedCategories[category.id] ? 'Réduire' : 'Voir'}
            </button>
          </div>
          {expandedCategories[category.id] && (
            <div className="pl-2 mt-2 space-y-2">
              {tasks
                .filter(task => task.category === category.id)
                .map(task => (
                  <label key={task.id} className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                    <input
                      type="checkbox"
                      checked={contract.tasksPerPeriod[timeSlot.id].includes(task.id)}
                      onChange={() => handleTaskChange(task.id, timeSlot.id)}
                      className="mr-2 text-blue-600 dark:text-blue-400 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-600 rounded"
                      disabled={!canEditContracts}
                    />
                    {task.name}
                  </label>
                ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );

  // Nouvelle fonction pour afficher les tâches cochées
  const renderCheckedTasks = () => {
    const allCheckedTasks = TIME_SLOTS.flatMap(slot =>
      contract.tasksPerPeriod[slot.id].map(taskId => {
        const task = tasks.find(t => t.id === taskId);
        return task ? { ...task, timeSlot: slot.label } : null;
      })
    ).filter(Boolean);

    return (
      <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
        <h3 className="text-md font-semibold mb-3 text-blue-800 dark:text-blue-300">Tâches sélectionnées</h3>
        <ul className="space-y-1">
          {allCheckedTasks.map((task, index) => (
            <li key={index} className="text-sm text-blue-700 dark:text-blue-400">
              <span className="font-medium">{task.name}</span> - <span className="text-blue-600 dark:text-blue-300">{task.timeSlot}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header avec navigation */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-3 mb-2">
                <button
                  onClick={() => window.history.back()}
                  className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                  title="Retour"
                >
                  <ArrowLeft size={20} />
                </button>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Gestion des contrats</h2>
              </div>
              <p className="text-gray-600 dark:text-gray-400">
                Contrat de <span className="font-medium text-gray-900 dark:text-white">{client.firstName} {client.lastName}</span>
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${client.isActive !== false
                ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300'
                : 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300'
                }`}>
                {client.isActive !== false ? 'Actif' : 'Inactif'}
              </span>
            </div>
          </div>
        </div>

        {/* Formulaire principal */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Section informations de base */}
            <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Informations de base</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Date de début</label>
                  <input
                    type="date"
                    name="startDate"
                    value={contract.startDate}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                    readOnly={!canEditContracts}
                  />
                </div>
                {!contract.isOpenEnded && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Date de fin</label>
                    <input
                      type="date"
                      name="endDate"
                      value={contract.endDate}
                      onChange={handleInputChange}
                      className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      readOnly={!canEditContracts}
                    />
                  </div>
                )}
                <div className="md:col-span-2">
                  <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                    <input
                      type="checkbox"
                      name="isOpenEnded"
                      checked={contract.isOpenEnded}
                      onChange={handleInputChange}
                      className="mr-2 text-blue-600 dark:text-blue-400 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-600 rounded"
                      disabled={!canEditContracts}
                    />
                    Contrat sans fin
                  </label>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tarif horaire (€)</label>
                  <input
                    type="number"
                    name="hourlyRate"
                    value={contract.hourlyRate}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    step="0.01"
                    required
                    readOnly={!canEditContracts}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Heures par mois</label>
                  <input
                    type="number"
                    name="hoursPerMonth"
                    value={contract.hoursPerMonth}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                    readOnly={!canEditContracts}
                  />
                </div>
              </div>
            </div>

            {/* Section paramètres tarifaires */}
            <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Paramètres tarifaires</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">

                {/* TYPE DE CONTRAT */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Type de contrat</label>
                  <select
                    name="contractType"
                    value={contract.contractType}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    readOnly={!canEditContracts}
                  >
                    <option value="prestation">Prestation</option>
                    <option value="mandat">Mandat</option>
                    <option value="mise_en_relation">Mise en relation</option>
                  </select>
                </div>

                {/* PRESTATIONS SOUSCRITES (Liste dynamique) */}
                <div className="md:col-span-2 lg:col-span-3 mt-4">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="text-md font-semibold text-gray-900 dark:text-white">Prestations souscrites</h4>
                    <button
                      type="button"
                      onClick={addPrestation}
                      disabled={!canEditContracts}
                      className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 text-sm font-medium transition-colors"
                    >
                      <Plus size={16} className="mr-1" />
                      Ajouter une prestation
                    </button>
                  </div>

                  {contract.prestations.length === 0 ? (
                    <div className="text-center p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg text-gray-500 dark:text-gray-400">
                      Aucune prestation configurée. Cliquez sur "Ajouter" pour commencer.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {contract.prestations.map((prestation, index) => {
                        const tarifRef = TARIFS_REFERENCE[prestation.type];
                        const tauxHoraire = tarifRef
                          ? (prestation.baseRateType === '70plus' ? tarifRef.plus70 : tarifRef.standard)
                          : 0;

                        return (
                          <div key={prestation.id} className="flex flex-wrap md:flex-nowrap gap-4 items-end p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">

                            {/* Type de prestation */}
                            <div className="flex-grow min-w-[200px]">
                              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Type de prestation</label>
                              <select
                                value={prestation.type}
                                onChange={(e) => updatePrestation(prestation.id, 'type', e.target.value)}
                                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                                disabled={!canEditContracts}
                              >
                                {Object.entries(TARIFS_REFERENCE).map(([key, config]) => (
                                  <option key={key} value={key}>{config.label}</option>
                                ))}
                              </select>
                            </div>

                            {/* Volume horaire */}
                            <div className="w-32">
                              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Vol. Hebdo (h)</label>
                              <input
                                type="number"
                                min="0"
                                step="0.5"
                                value={prestation.weeklyHours}
                                onChange={(e) => updatePrestation(prestation.id, 'weeklyHours', parseFloat(e.target.value) || 0)}
                                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                                disabled={!canEditContracts}
                              />
                            </div>

                            {/* Tarif applicable */}
                            <div className="w-48">
                              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Tarif applicable</label>
                              <select
                                value={prestation.baseRateType}
                                onChange={(e) => updatePrestation(prestation.id, 'baseRateType', e.target.value)}
                                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 text-sm"
                                disabled={!canEditContracts}
                              >
                                <option value="standard">Standard</option>
                                <option value="70plus">Exonéré 70+</option>
                              </select>
                            </div>

                            {/* Info Tarif & Mandat */}
                            <div className="flex flex-col justify-center text-xs text-gray-500 dark:text-gray-400 min-w-[120px]">
                              <span>Tarif: <strong>{tauxHoraire.toFixed(2)} €/h</strong></span>
                              <span>Mandat: <strong>{calculateOmadFees(prestation.weeklyHours).toFixed(2)} €/h</strong></span>
                            </div>

                            {/* Bouton Supprimer */}
                            {canEditContracts && (
                              <button
                                type="button"
                                onClick={() => removePrestation(prestation.id)}
                                className="p-2 text-red-600 hover:text-red-800 hover:bg-red-100 rounded transition-colors"
                                title="Supprimer cette prestation"
                              >
                                <Trash size={18} />
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Majorations applicables */}
                <div className="md:col-span-2 lg:col-span-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Majorations applicables</label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <label className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        name="majorations.weekend"
                        checked={contract.majorations?.weekend || false}
                        onChange={(e) => setContract(prev => ({
                          ...prev,
                          majorations: {
                            ...(prev.majorations || {}),
                            weekend: e.target.checked
                          }
                        }))}
                        className="mr-3 text-blue-600 dark:text-blue-400 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-600 rounded"
                        disabled={!canEditContracts}
                      />
                      Dimanche (+25%)
                    </label>
                    <label className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        name="majorations.holidays"
                        checked={contract.majorations?.holidays || false}
                        onChange={(e) => setContract(prev => ({
                          ...prev,
                          majorations: {
                            ...(prev.majorations || {}),
                            holidays: e.target.checked
                          }
                        }))}
                        className="mr-3 text-blue-600 dark:text-blue-400 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-600 rounded"
                        disabled={!canEditContracts}
                      />
                      Jours fériés (+50%)
                    </label>
                    <label className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        name="majorations.may1"
                        checked={contract.majorations?.may1 || false}
                        onChange={(e) => setContract(prev => ({
                          ...prev,
                          majorations: {
                            ...(prev.majorations || {}),
                            may1: e.target.checked
                          }
                        }))}
                        className="mr-3 text-blue-600 dark:text-blue-400 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-600 rounded"
                        disabled={!canEditContracts}
                      />
                      1er mai (+100%)
                    </label>
                  </div>
                </div>

                {/* Conditions de résiliation et modification */}
                <div className="md:col-span-2 lg:col-span-3 border-t border-gray-200 dark:border-gray-700 pt-6 mt-4">
                  <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">Conditions contractuelles</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Préavis de résiliation (jours)</label>
                      <input
                        type="number"
                        name="termination.noticeDays"
                        value={contract.termination.noticeDays}
                        onChange={(e) => setContract(prev => ({
                          ...prev,
                          termination: { ...prev.termination, noticeDays: parseInt(e.target.value) || 30 }
                        }))}
                        className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        min="1"
                        readOnly={!canEditContracts}
                      />
                      <span className="text-xs text-gray-600 dark:text-gray-400 mt-1 block">
                        Conditions: {contract.termination.conditions}
                      </span>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Préavis modification (heures)</label>
                      <input
                        type="number"
                        name="modifications.noticeHours"
                        value={contract.modifications.noticeHours}
                        onChange={(e) => setContract(prev => ({
                          ...prev,
                          modifications: { ...prev.modifications, noticeHours: parseInt(e.target.value) || 48 }
                        }))}
                        className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        min="1"
                        readOnly={!canEditContracts}
                      />
                      <span className="text-xs text-gray-600 dark:text-gray-400 mt-1 block">
                        Conditions: {contract.modifications.conditions}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tâches groupées par créneaux horaires */}
            <div className="border-b border-gray-200 dark:border-gray-700 pb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Tâches à accomplir par créneau horaire</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {TIME_SLOTS.map((slot) => (
                  <div key={slot.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <h4 className="font-bold text-gray-900 dark:text-white text-sm mb-3">
                      {slot.label} <span className="text-gray-600 dark:text-gray-400 text-xs">({slot.timeRange})</span>
                    </h4>
                    {renderTasksByTimeSlot(slot)}
                  </div>
                ))}
              </div>
            </div>

            {/* Affichage des tâches cochées */}
            {renderCheckedTasks()}

            {/* Bouton de sauvegarde */}
            {canEditContracts && (
              <div className="flex justify-end pt-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="submit"
                  className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  <Save size={20} className="mr-2" />
                  Sauvegarder le contrat
                </button>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default ClientContract;
